/* eslint-disable @next/next/no-img-element */
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useState, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import H2 from '@base/H2/H2';
import H4 from '@base/H4/H4';
import P2 from '@base/P2/P2';
import Paper from '@base/Paper/Paper';
import { mobile, Spacings, Fonts } from '@styles/index';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import useSubscribed from '@hooks/useSubscribed';
import { fetchMyInfo } from '@actions/auth';
import {
  enrollForContest,
  fetchUpcomingCombatsInfo,
  updateContestOnboardingPreference
} from '@actions/combats';
import Dynamic from '@base/Dynamic/Dynamic';
import { COMBAT_ENROLLED } from '@constants/segment';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { getImage } from '@utils/imageHelper';
import { getIsEnrolled } from '@selectors/combats';
import { getTopologyDataByUID } from '@selectors/topologies';
import useAccessControl from '@api/hooks/auth/useAccessControl';
import { getUserOnboardingPreference } from '@api/selectors/auth';
import EnrollFlow from './EnrollFlow/EnrollFlow';
import CombatCard from '../Common/CombatCard/CombatCard';
import CombatCardNavigation from './CombatCardNavigation';
import useCombatSegmentAnalytics from '../Common/useCombatSegmentAnalytics';

const Modals = Dynamic(() => import('./Modals'));

const Wrapper = styled.div`
  text-align: center;
  max-width: 1136px;
  margin: 160px auto;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    margin-top: ${Spacings.SPACING_20B};
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
  }
`;

const Image = styled.img`
  margin: auto;
  height: 50px;
  width: 50px;
  max-width: 50px;
`;

const Title = styled(H2)`
  margin-top: ${Spacings.SPACING_6B};
  @media only screen and (max-width: ${mobile.maxWidth}) {
    font-size: ${Fonts.H3};
  }
`;

const Descripiton = styled(H4)`
  width: 540px;
  margin: auto;
  margin-top: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_14B};
  font-weight: ${Fonts.REGULAR};
  @media only screen and (max-width: ${mobile.maxWidth}) {
    width: 100%;
    font-size: ${Fonts.H5};
  }
`;

const CombatsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  grid-gap: ${Spacings.SPACING_4B};
  justify-content: ${({ $hasMultipleCategories }) =>
    $hasMultipleCategories ? 'space-between' : 'center'};
`;

const NoDataContainer = styled(Paper)`
  display: flex;
  width: 788px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 0px auto ${Spacings.SPACING_11B};

  img {
    margin-bottom: ${Spacings.SPACING_5B};
  }

  @media only screen and (max-width: ${mobile.maxWidth}) {
    width: 100%;
    padding: ${Spacings.SPACING_12B} ${Spacings.SPACING_6B};
  }
`;

const CombatCardWrapper = styled.div`
  text-align: start;
  width: 788px;

  @media only screen and (max-width: ${mobile.maxWidth}) {
    width: 100%;
  }
`;

const StyledDivider = styled.div`
  background-color: var(--color-i-blue);
  width: 288px;
  height: 1px;

  @media only screen and (max-width: ${mobile.maxWidth}) {
    width: 58px;
  }
`;

const CombatInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    margin-left: ${Spacings.SPACING_4B};
    margin-right: ${Spacings.SPACING_2B};
  }
`;

const Info = styled(P2)`
  font-weight: ${Fonts.SEMIBOLD};
  margin-right: ${Spacings.SPACING_4B};
`;

const BottomTag = ({ promoTag, promotionalLine }) => (
  <CombatInfoWrapper>
    <StyledDivider />
    <img src={promoTag} alt="New Combats" />
    <Info color="var(--color-i-orange-1)">{promotionalLine}</Info>
    <StyledDivider />
  </CombatInfoWrapper>
);

BottomTag.propTypes = {
  promoTag: PropTypes.string.isRequired,
  promotionalLine: PropTypes.string.isRequired
};

const Empty = () => (
  <NoDataContainer>
    <img src={getImage('educator/missing.svg')} alt="No Combats" />
    <P2>Upcoming Combats have not been scheduled yet.</P2>
    <P2>We’ll notify you once details are available.</P2>
  </NoDataContainer>
);

const CombatsInfo = () => {
  const router = useRouter();
  const segmentAnalytics = useCombatSegmentAnalytics();
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);

  const { goalUID, goalSlug } = router.query;
  const goal = useSelectorWithShallowEqual(getTopologyDataByUID(goalUID));

  const data = useSelector(({ combats }) => combats.staticData[goalUID] || {});

  const combatsInfo = useSelector(
    ({ combats }) =>
      combats.upcoming?.[goalUID]?.results?.filter(
        ({ programmes }) => programmes?.length
      ) || []
  );

  const navigationItems = useMemo(
    () =>
      combatsInfo?.map(({ uid, description }) => ({
        uid,
        label: description
      })),
    [combatsInfo]
  );

  const selectedCombatRef = useRef('');

  const dispatch = useDispatch();

  const isLoggedIn = useSelector(({ auth }) => auth.isLoggedIn);
  const [showEnrollFlow, setShowEnrollFlow] = useState(false);
  const closeFlow = () => setShowEnrollFlow(false);
  const inviteCodeRef = useRef('');

  const isEnrolled = useSelectorWithShallowEqual(
    getIsEnrolled(selectedCombatRef.current?.uid)
  );

  const isSubscribed = useSubscribed(goalUID);
  const { accessControl } = useAccessControl();
  const accessControlClassPreference =
    getUserOnboardingPreference(accessControl) || {};
  const enroll = async (inviteCode = '') => {
    inviteCodeRef.current = inviteCode;
    const selectedClass = combatsInfo?.[selectedCategoryIndex];

    if (accessControlClassPreference.uid !== selectedClass.uid) {
      await dispatch(
        updateContestOnboardingPreference({
          goalUID,
          optionUID: selectedClass.uid,
          selectedClass,
          preferenceType: 1
        })
      );
    }
    return dispatch(
      enrollForContest({
        contestUID: selectedCombatRef.current.uid,
        inviteCode
      })
    ).then(async (res) => {
      if (!res?.error) {
        segmentAnalytics(COMBAT_ENROLLED, {
          combat_invite_code: inviteCodeRef.current,
          credits_applied: selectedCombatRef.current.enroll_credits,
          combat_class: navigationItems[selectedCategoryIndex]?.label || 'NA',
          combat_uid: selectedCombatRef.current.uid,
          combat_title: selectedCombatRef.current.name,
          combat_start_date: selectedCombatRef.current.starts_at,
          combat_duration:
            selectedCombatRef.current?.quiz_details?.duration || 0,
          combat_user_level: 'NA',
          combat_user_state: 'NA',
          goal_name: goal.name,
          is_enrolled: isEnrolled,
          last_primary_source_section: 'NA'
        });
      }

      await dispatch(fetchMyInfo(router.pathname));
      closeFlow();
      return res;
    });
  };

  useFirstRenderSkip(() => {
    dispatch(fetchUpcomingCombatsInfo(goalUID));
  }, [isLoggedIn]);

  const hasMultipleCategories = combatsInfo?.length > 1;
  const isEmpty = !combatsInfo?.length;

  return (
    <>
      {showEnrollFlow && (
        <EnrollFlow
          show={showEnrollFlow}
          closeFlow={closeFlow}
          isSubscribed={isSubscribed}
          enroll={enroll}
          goalUID={goalUID}
          goalSlug={goalSlug}
          selectedCombatInfo={selectedCombatRef.current}
        />
      )}
      <Wrapper id="contests-enroll">
        <Image src={data.upcomingCombatLogo} alt="upcoming logo" />
        <Title>{data.upcomingCombatTitle}</Title>
        <Descripiton color="var(--color-text-secondary)">
          {data.upcomingCombatDescription}
        </Descripiton>
        {isEmpty ? (
          <>
            <Empty />
            <BottomTag
              promoTag={data.promoTag}
              promotionalLine={data.promotionalLine}
            />
          </>
        ) : (
          <CombatsContainer $hasMultipleCategories={hasMultipleCategories}>
            {hasMultipleCategories && (
              <CombatCardNavigation
                currentIndex={selectedCategoryIndex}
                items={navigationItems}
                onItemClick={(index) => setSelectedCategoryIndex(index)}
              />
            )}
            <CombatCardWrapper>
              {combatsInfo?.[selectedCategoryIndex].programmes.map((combat) => (
                <CombatCard key={combat.uid} data={combat} />
              ))}
              <BottomTag
                promoTag={data.promoTag}
                promotionalLine={data.promotionalLine}
              />
            </CombatCardWrapper>
          </CombatsContainer>
        )}
      </Wrapper>
      <Modals />
    </>
  );
};

export default CombatsInfo;
